import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  PiDrop, PiWifiSlash, PiChatCircleDots, PiLightning, PiFlask, PiNotePencil, PiGauge, PiThermometerSimple,
  PiDropHalf, PiFan, PiLightbulb, PiShieldCheck, PiPlant, PiCalendarBlank, PiWarningCircle,
} from 'react-icons/pi';
import { statusColor } from '../../config/sensorRanges';
import { Panel, PanelTitle, Note } from './ui';
import { alpha } from './helpers';

/* One row per cause: an icon tile tinted by severity, the cause in bold, the
   specifics (which racks, how late) underneath, and the fix as a button. */
const Items = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
`;

const ItemRow = styled.li`
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  & + & { border-top: 1px solid ${p => p.theme.colors.border}; }

  .ico {
    width: 34px; height: 34px;
    border-radius: 10px;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 18px;
    color: ${p => (p.$status === 'notice' ? p.theme.colors.warningHover : statusColor(p.theme, p.$status))};
    background: ${p => alpha(statusColor(p.theme, p.$status), 0.14)};
  }
  .what { min-width: 0; }
  .what b { display: block; font-weight: 500; line-height: 1.3; }
  .what span { display: block; margin-top: 1px; font-size: 13px; color: ${p => p.theme.colors.textSecondary}; line-height: 1.35; }
  .fix {
    min-height: 0;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid ${p => p.theme.colors.border};
    font-size: 12px;
    font-weight: 500;
    color: ${p => p.theme.colors.text};
    text-decoration: none;
    white-space: nowrap;
    transition: background 0.12s ease;
    &:hover { background: ${p => p.theme.colors.surfaceHover}; }
    &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 1px; }
  }
`;

const Summary = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${p => p.theme.colors.textMuted};
  b { font-weight: 500; color: ${p => (p.$urgent ? p.theme.colors.critical : p.theme.colors.text)}; }
`;

// How a cause reads when several racks share it, and its icon.
const KINDS = {
  water: { title: 'Water change overdue', icon: PiDrop },
  device: { title: 'Offline', icon: PiWifiSlash },
  'probe-silent': { title: 'Tank probe silent', icon: PiGauge },
  tank: { title: 'Out of the safe range', icon: PiFlask },
  unchecked: { title: 'Not checked by hand lately', icon: PiNotePencil },
  spray: { title: 'Spray due', icon: PiShieldCheck },
  crop: { title: 'Behind schedule', icon: PiCalendarBlank },
  bot: { icon: PiChatCircleDots },
  breaker: { icon: PiLightning },
  'probe-range': { icon: PiGauge },
  climate: { icon: PiThermometerSimple },
  humidity: { icon: PiDropHalf },
  balancer: { icon: PiFan },
  'light-unconfirmed': { icon: PiLightbulb },
  'light-manual': { icon: PiLightbulb },
  'light-unset': { icon: PiLightbulb },
  plants: { icon: PiPlant },
};
const cap = t => (t ? t[0].toUpperCase() + t.slice(1) : t);

/* Same-cause items become one row. Worst severity wins; the row links where its
   first (worst) member links. Kinds without a group title stay one row each. */
function groupItems(items) {
  const rows = [];
  const byKind = {};
  items.forEach((i) => {
    const k = KINDS[i.kind];
    if (k?.title) {
      if (byKind[i.kind]) { byKind[i.kind].members.push(i); return; }
      byKind[i.kind] = { ...i, members: [i] };
      rows.push(byKind[i.kind]);
    } else {
      rows.push({ ...i, members: [i] });
    }
  });
  return rows.map((r) => {
    const k = KINDS[r.kind] || {};
    if (k.title) {
      return r.members.length > 1
        ? { ...r, title: k.title, detail: r.members.map(m => m.short).join(', '), icon: k.icon }
        : { ...r, title: `${r.subject}: ${k.title.toLowerCase()}`, detail: cap(r.text), icon: k.icon };
    }
    return { ...r, title: r.subject, detail: cap(r.text), icon: k.icon || PiWarningCircle };
  });
}

const MoreButton = styled.button`
  margin-top: 8px;
  padding: 2px 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  color: ${p => p.theme.colors.primary};
  cursor: pointer;
`;

/* What needs a person: problems grouped by cause, worst first, the fix as a button. */
export default function NeedsYou({ items, order }) {
  const [showAll, setShowAll] = useState(false);

  const urgent = items.filter(i => i.severity !== 'notice');
  const groups = groupItems(items);
  const visibleGroups = showAll ? groups : groups.slice(0, 5);

  return (
    <Panel $order={order}>
      <PanelTitle>
        Needs you
        {items.length > 0 && (
          <Summary $urgent={urgent.length > 0}>
            {urgent.length > 0 && <><b>{urgent.length} urgent</b>{items.length > urgent.length ? ', ' : ''}</>}
            {items.length > urgent.length && `${items.length - urgent.length} to check`}
          </Summary>
        )}
      </PanelTitle>
      {groups.length === 0 ? (
        <Note>Nothing. Every tank, device and plant is where it should be.</Note>
      ) : (
        <>
          <Items>
            {visibleGroups.map(g => (
              <ItemRow key={g.id} $status={g.severity}>
                <span className="ico" aria-hidden="true"><g.icon /></span>
                <span className="what"><b>{g.title}</b><span>{g.detail}</span></span>
                {g.to && <Link className="fix" to={g.to}>{g.fix || 'Open'}</Link>}
              </ItemRow>
            ))}
          </Items>
          {groups.length > 5 && (
            <MoreButton onClick={() => setShowAll(v => !v)}>
              {showAll ? 'Show fewer' : `Show ${groups.length - 5} more`}
            </MoreButton>
          )}
        </>
      )}
    </Panel>
  );
}
